import React, { useState, useEffect } from 'react';
import SubsectionTitle from './SubsectionTitle';
import { validateCUI } from '../utils/functions';

export default function InvoiceSelector({ settings, cartAttributes, setCartAttributes, gadgetApi }) {
	const [errorMessage, setErrorMessage] = useState(null);
	const [cui, setCui] = useState(cartAttributes.cui);
	const [detailsCompany, setDetailsCompany] = useState(null);

	useEffect(() => {
		setCui(cartAttributes.cui);

		if (!cartAttributes.cui) {
			setDetailsCompany(null);

			return;
		}

		const companiesStr = localStorage.getItem('companies');

		if (companiesStr) {
			try {
				const companies = JSON.parse(companiesStr);

				if (companies && companies.length > 0) {
					const normalizedCui = cartAttributes.cui.replace(/RO/gi, '').replace(/\s/g, '').trim();

					setDetailsCompany(
						companies.find(
							(company) =>
								company.cod_fiscal === normalizedCui || company.cod_fiscal === cartAttributes.cui
						)
					);
				}
			} catch (error) {
				console.error('Error parsing companies from localStorage:', error);
			}
		}
	}, [cartAttributes.cui]);

	const handleInvoiceTypeChange = (type) => {
		setCartAttributes((prev) => ({
			...prev,
			invoiceType: type,
			cui: type === 'pf' ? null : cui,
		}));
	};

	const normalizeCui = (cuiValue) => {
		if (!cuiValue) return '';

		return cuiValue.replace(/RO/gi, '').replace(/\s/g, '').trim();
	};

	const handleCheckCui = async (type) => {
		setErrorMessage(null);

		if (!cui || cui.trim() === '') {
			setErrorMessage(settings.cuiRequired || 'CUI is required');

			return;
		}

		if (type === 'ro') {
			const isCuiValid = validateCUI(cui, settings);

			if (!isCuiValid.valid) {
				setErrorMessage(isCuiValid.message);
				setCartAttributes((prev) => ({
					...prev,
					cui: null,
				}));
				setDetailsCompany(null);

				return;
			}

			try {
				const existingCompaniesStr = localStorage.getItem('companies');
				const companies = existingCompaniesStr ? JSON.parse(existingCompaniesStr) : [];
				const normalizedCui = normalizeCui(cui);

				const companyExistsInLocalStorage = companies.find(
					(company) => company.cod_fiscal === normalizedCui || company.cod_fiscal === cui
				);

				let details = null;

				if (!companyExistsInLocalStorage) {
					const result = await gadgetApi.cuiValidator({ companyNumber: cui });

					if (result && result.data) {
						const updatedCompanies = [...companies, result.data];

						localStorage.setItem('companies', JSON.stringify(updatedCompanies));
						details = result.data;
					} else {
						setErrorMessage(settings.cuiValidationFailed || 'Failed to validate CUI');

						return;
					}
				} else {
					details = companyExistsInLocalStorage;
				}

				setCartAttributes((prev) => ({
					...prev,
					cui: cui,
				}));

				setDetailsCompany(details);
			} catch (error) {
				console.error('Error validating CUI:', error);

				setErrorMessage(settings.cuiValidationError || 'An error occurred while validating CUI');
				setCartAttributes((prev) => ({
					...prev,
					cui: null,
				}));

				setDetailsCompany(null);
			}
		} else {
			setCartAttributes((prev) => ({
				...prev,
				cui: cui,
			}));

			setDetailsCompany(null);
		}
	};

	return (
		<div className="invoice-selector">
			<SubsectionTitle settings={settings} stepNumber={4} cartAttributes={cartAttributes} />

			<div className="invoice-buttons">
				<button
					className={`invoice-button button button--lg ${
						cartAttributes.invoiceType === 'pf' ? 'button--active' : ''
					}`}
					onClick={() => handleInvoiceTypeChange('pf')}
				>
					{settings.buttonInvoiceLabelPf}
				</button>

				<button
					className={`invoice-button button button--lg ${
						cartAttributes.invoiceType === 'pj' ? 'button--active' : ''
					}`}
					onClick={() => handleInvoiceTypeChange('pj')}
				>
					{settings.buttonInvoiceLabelPj}
				</button>
			</div>

			{cartAttributes.invoiceType === 'pj' && (
				<div className="invoice-pj-form__container can-be-closed">
					<div className="invoice-pj-form">
						<div class="form-control form-control--normal-label">
							<label for="cui" class="normal-label">
								{settings.cuiFieldLabel}

								<span class="required-asterisk">*</span>
							</label>

							<input
								class="input"
								name="cui"
								type="text"
								placeholder=" "
								value={cui}
								onChange={(e) => setCui(e.target.value)}
							/>
						</div>
						<button className="button button--lg" onClick={() => handleCheckCui('ro')}>
							{settings.addRomanianCui}
						</button>
						<button className="button button--lg" onClick={() => handleCheckCui('int')}>
							{settings.addInternationalCui}
						</button>
					</div>

					{errorMessage && <p className="text-error">{errorMessage}</p>}

					{cartAttributes.cui ? (
						<div className="invoice-cui-form__details">
							<p className="invoice-cui-form__details-title">{settings.invoiceCompanyDetails}</p>
							<p className="text-base">
								<span className="text-subdued">{settings.cuiLabel}</span> {cartAttributes.cui}
							</p>
							{detailsCompany && (
								<>
									<p className="text-base">
										<span className="text-subdued">{settings.companyName}</span>{' '}
										{detailsCompany.nume}
									</p>
									<p className="text-base">
										<span className="text-subdued">{settings.registrationCode}</span>{' '}
										{detailsCompany.cod_inmatriculare}
									</p>
									<p className="text-base">
										<span className="text-subdued">{settings.companyAddress}</span>{' '}
										{detailsCompany.adresa}
									</p>
								</>
							)}
						</div>
					) : (
						cartAttributes.invoiceType === 'pj' && (
							<div className="invoice-cui-form__details">
								<p className="invoice-cui-form__details-title">{settings.noCompanyAdded}</p>
							</div>
						)
					)}
				</div>
			)}
		</div>
	);
}
